import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { uploadMediaFile } from '@/hooks/useUpload';
import { isMarketplaceAccount, isMarketplaceTheme } from '@/lib/marketplace';
import { fetchMarketplaceCategories, type MarketplaceCategory } from '@/lib/marketplaceCatalog';
import { resolveStoreName } from '@/lib/display';

/**
 * Publication d'une annonce par un simple visiteur, sans création de compte.
 * Règle marketplace : UNE seule image, utilisée à la fois comme photo de
 * couverture et comme photo additionnelle. Pour plusieurs photos, il faut
 * créer un compte vendeur gratuit.
 */
export default function PublierAnnoncePage() {
  const { tenant, theme, isTenant, loading } = useTenant();
  const navigate = useNavigate();
  const marketplaceId = isTenant && tenant ? Number(tenant.id) : 0;
  const storeName = resolveStoreName(tenant, theme);
  const isMarketplace = Boolean(tenant) && (isMarketplaceTheme(theme) || isMarketplaceAccount(tenant?.type, tenant?.typecompte));

  const [categories, setCategories] = useState<MarketplaceCategory[]>([]);
  const [form, setForm] = useState({ titre: '', description: '', prix: '', ville: '', telephone: '', owner: '', idcategorie: '' });
  const [image, setImage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<number | null>(null);

  useEffect(() => {
    document.title = `Publier une annonce — ${storeName}`;
  }, [storeName]);

  useEffect(() => {
    if (!marketplaceId) return;
    fetchMarketplaceCategories(marketplaceId).then(setCategories).catch(() => setCategories([]));
  }, [marketplaceId]);

  useEffect(() => {
    if (!form.ville && tenant?.Ville) setForm((f) => ({ ...f, ville: tenant.Ville, telephone: f.telephone || tenant.telephone || '' }));
  }, [tenant, form.ville]);

  const rootCategories = useMemo(() => categories.filter((c) => !c.parent_id), [categories]);

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    if (!file.type.startsWith('image/')) {
      setError('Seules les images sont acceptées.');
      return;
    }
    setUploading(true);
    try {
      const url = await uploadMediaFile(file, 'marketplace-annonces');
      setImage(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "L'image n'a pas pu être envoyée.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!marketplaceId) return;
    setError(null);
    if (form.titre.trim().length < 5) { setError('Le titre doit contenir au moins 5 caractères.'); return; }
    if (form.description.trim().length < 20) { setError('Décrivez votre annonce en au moins 20 caractères.'); return; }
    if (!form.telephone.trim()) { setError('Un numéro de téléphone est nécessaire pour être contacté.'); return; }
    if (!image) { setError('Ajoutez une photo (une seule image pour une annonce sans compte).'); return; }

    setSubmitting(true);
    try {
      const { data, error: insertError } = await supabase
        .from('annonce')
        .insert({
          titre: form.titre.trim(),
          type: '',
          description: form.description.trim(),
          prix: form.prix.trim(),
          ville: form.ville.trim(),
          pays: tenant?.Pays || '',
          telephone: form.telephone.trim(),
          image,
          video: '',
          idcategorie: form.idcategorie ? Number(form.idcategorie) : null,
          idvendeur: marketplaceId,
          owner: form.owner.trim() || 'Visiteur',
          sp: 'public',
          publique: 1,
          disponible: true,
          is_boost: false,
          is_vendor_featured: false,
          visibilite: 'public',
          pourcentage: '',
          click: '0',
          created: new Date().toISOString(),
        })
        .select('id')
        .single();

      if (insertError) throw insertError;
      const newId = Number((data as { id?: number } | null)?.id || 0);

      // La même image sert de photo additionnelle (règle 1 photo sans compte).
      if (newId && image) {
        await supabase.from('annonces_images').insert({ idannonce: newId, image }).then(() => undefined, () => undefined);
      }
      setCreatedId(newId || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "La publication a échoué.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-background-50">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  if (!isMarketplace) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 bg-background-50">
        <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-megaphone-line text-2xl text-foreground-400"></i>
        </div>
        <h1 className="text-xl font-bold font-heading text-foreground-950 mb-2">Publication réservée aux marketplaces</h1>
        <p className="text-sm text-foreground-500 max-w-md mb-6">
          Ce site n&apos;accepte pas les annonces de visiteurs. Retournez à l&apos;accueil de {storeName}.
        </p>
        <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium no-underline whitespace-nowrap">
          Retour à l&apos;accueil
        </Link>
      </div>
    );
  }

  if (createdId) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4 bg-background-50">
        <div className="max-w-lg w-full bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-green-100 text-green-700 flex items-center justify-center mx-auto mb-4">
            <i className="ri-checkbox-circle-line text-2xl"></i>
          </div>
          <h1 className="text-lg font-bold font-heading text-foreground-950 mb-2">Annonce publiée</h1>
          <p className="text-sm text-foreground-500 mb-5">
            Votre annonce est en ligne sur {storeName}. Elle utilise une seule photo (couverture et galerie).
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
            <Link to={`/annonce/${createdId}`} className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium no-underline whitespace-nowrap">
              Voir mon annonce
            </Link>
            <Link to="/annonces" className="px-5 py-2.5 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium no-underline whitespace-nowrap hover:bg-background-100">
              Toutes les annonces
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 pb-16">
      <section className="relative py-10 md:py-14">
        <div className="absolute inset-0 bg-gradient-to-b from-background-100/60 to-transparent"></div>
        <div className="relative w-full px-4 md:px-6 max-w-3xl mx-auto text-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-[11px] font-semibold uppercase tracking-wider mb-3">
            <i className="ri-megaphone-line"></i>
            Publier sans compte
          </span>
          <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">Publier une annonce</h1>
          <p className="text-sm text-foreground-500 max-w-xl mx-auto">
            Gratuit et sans inscription. Une seule photo : elle sert de couverture et de photo additionnelle.
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 max-w-3xl mx-auto">
        <form onSubmit={submit} className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="min-w-0 sm:col-span-2">
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Titre de l&apos;annonce *</label>
              <input
                type="text"
                value={form.titre}
                onChange={(e) => setForm({ ...form, titre: e.target.value.slice(0, 120) })}
                placeholder="Ex : Vélo de ville en très bon état"
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Catégorie</label>
              <select
                value={form.idcategorie}
                onChange={(e) => setForm({ ...form, idcategorie: e.target.value })}
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
              >
                <option value="">Sans catégorie</option>
                {rootCategories.map((c) => (
                  <option key={c.id} value={c.id}>{c.nom}</option>
                ))}
              </select>
            </div>
            <div className="min-w-0">
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Prix (optionnel)</label>
              <input
                type="text"
                value={form.prix}
                onChange={(e) => setForm({ ...form, prix: e.target.value.slice(0, 40) })}
                placeholder="Ex : 850 MAD"
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Ville</label>
              <input
                type="text"
                value={form.ville}
                onChange={(e) => setForm({ ...form, ville: e.target.value.slice(0, 80) })}
                placeholder="Ex : Casablanca"
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Téléphone *</label>
              <input
                type="tel"
                value={form.telephone}
                onChange={(e) => setForm({ ...form, telephone: e.target.value.slice(0, 30) })}
                placeholder="06 00 00 00 00"
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Votre nom</label>
              <input
                type="text"
                value={form.owner}
                onChange={(e) => setForm({ ...form, owner: e.target.value.slice(0, 80) })}
                placeholder="Optionnel"
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
              />
            </div>
            <div className="min-w-0 sm:col-span-2">
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Description *</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value.slice(0, 900) })}
                rows={4}
                placeholder="Décrivez votre article, son état, les détails utiles…"
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 resize-none"
              />
              <p className="text-[11px] text-foreground-400 mt-1">{form.description.length}/900 caractères</p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Photo de l&apos;annonce * (une seule)</label>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="w-24 h-24 rounded-lg bg-background-100 border border-background-200/70 overflow-hidden flex items-center justify-center flex-shrink-0">
                {image ? (
                  <img src={image} alt="Aperçu de l'annonce" className="w-full h-full object-cover object-top" />
                ) : uploading ? (
                  <i className="ri-loader-4-line animate-spin text-foreground-400"></i>
                ) : (
                  <i className="ri-image-line text-foreground-300 text-xl"></i>
                )}
              </div>
              <div className="min-w-0">
                <label className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-foreground-950 text-background-50 text-xs font-medium cursor-pointer hover:bg-foreground-800 transition-colors whitespace-nowrap">
                  <i className="ri-upload-cloud-line"></i>
                  {image ? 'Remplacer la photo' : 'Choisir une photo'}
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
                </label>
                <p className="text-[11px] text-foreground-400 mt-2 leading-relaxed">
                  Cette photo sera utilisée comme couverture et comme photo additionnelle.
                  Besoin de plusieurs photos ?{' '}
                  <Link to="/vendeur/inscription" className="text-primary-600 font-medium hover:text-primary-700">
                    Créez un compte vendeur gratuit
                  </Link>.
                </p>
              </div>
            </div>
          </div>

          {error && (
            <p className="text-xs text-red-600 flex items-start gap-1.5">
              <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>{error}
            </p>
          )}

          <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={submitting || uploading}
              className="w-full sm:w-auto px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? <><i className="ri-loader-4-line animate-spin"></i>Publication…</> : <><i className="ri-send-plane-fill"></i>Publier mon annonce</>}
            </button>
            <button type="button" onClick={() => navigate('/annonces')} className="text-xs text-foreground-500 hover:text-foreground-800 cursor-pointer">
              Annuler
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}